import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import {
    Button, Box, Text, Flex, Input,
    FormControl, FormLabel, NumberInput, NumberInputField, Divider, Icon,
} from '@chakra-ui/react';
import { AppDialog, DialogButton } from '../common/AppDialog';
import { Plus, FileText } from 'lucide-react';
import { SHEET_TEMPLATES, SHEET_STYLE_LABELS, SheetTemplate, SheetStyleId } from '../../config/sheetTemplates';
import { useBIMStore } from '../../state/useBIMStore';

interface NewSheetDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

/**
 * New Sheet dialog (BIM-style): a template gallery of pre-printed
 * (antetli) sheets with visual previews, sheet number/name inputs, and a
 * collapsible "New Paper Type" panel for custom sizes.
 */
export function NewSheetDialog({ isOpen, onClose }: NewSheetDialogProps) {
    const { t } = useTranslation();
    const project = useBIMStore((s) => s.project);
    const createSheet = useBIMStore((s) => s.createSheet);
    const setActiveSheet = useBIMStore((s) => s.setActiveSheet);
    const createCustomTitleBlock = useBIMStore((s) =>s.createCustomTitleBlock); const [selectedTemplate, setSelectedTemplate] = useState<SheetTemplate>(SHEET_TEMPLATES[0]);
    const [sheetNumber, setSheetNumber] = useState('');
    const [sheetName, setSheetName] = useState('');
    const [showNewPaper, setShowNewPaper] = useState(false);
    const [paperName, setPaperName] = useState('');
    const [paperWidth, setPaperWidth] = useState('594');
    const [paperHeight, setPaperHeight] = useState('420');

    // Merge built-in + user-created paper types, refreshing when the project's
    // customTitleBlocks array changes.
    const allTemplates = useMemo(() => {
        const customs: SheetTemplate[] = project.customTitleBlocks.map(tb => ({
            titleBlockId: tb.id,
            name: tb.name,
            sizeLabel: `${Math.round(tb.width)} x ${Math.round(tb.height)} mm`,
            widthMm: tb.width,
            heightMm: tb.height,
            style: 'standard' as SheetStyleId,
            description: 'Custom paper type.',
        }));
        return [...SHEET_TEMPLATES, ...customs];
    }, [project.customTitleBlocks]);

    // Reset the form each time the dialog opens
    useEffect(() => {
        if (!isOpen) return;
        setSheetNumber(nextSheetNumber());
        setSheetName('');
        setSelectedTemplate(SHEET_TEMPLATES[0]);
        setShowNewPaper(false);
    }, [isOpen, project.customTitleBlocks]);

    const nextSheetNumber = () => {
        const store = useBIMStore.getState();
        const existing = Array.from(store.project.sheets.values());
        const maxNum = existing.reduce((max, s) => {
            const num = parseInt(s.number.replace(/[^0-9]/g, ''), 10);
            return isNaN(num) ? max : Math.max(max, num);
        }, 100);
        return `A${maxNum + 1}`;
    };

    const handleCreate = () => {
        const number = sheetNumber.trim() || nextSheetNumber();
        const name = sheetName.trim() || 'Untitled';

        let titleBlockId = selectedTemplate.titleBlockId;
        let style: SheetTemplate['style'] = selectedTemplate.style;
        // Creating straight from the New Paper panel: register then use it
        if (showNewPaper && paperName.trim()) {
            const w = Math.max(100, parseFloat(paperWidth) || 594);
            const h = Math.max(100, parseFloat(paperHeight) || 420);
            createCustomTitleBlock(paperName.trim(), w, h);
            const created = useBIMStore.getState().project.customTitleBlocks.slice(-1)[0];
            if (created) titleBlockId = created.id;
            style = 'standard';
        }

        createSheet(number, name, titleBlockId, style);
        const createdSheet = Array.from(useBIMStore.getState().project.sheets.values())
            .find(s => s.number === number);
        if (createdSheet) {
            setActiveSheet(createdSheet.id);
            window.dispatchEvent(new CustomEvent('yap:zoom-sheet-fit'));
        }
        onClose();
    };

    const selectTemplate = (t: SheetTemplate) => {
        setSelectedTemplate(t);
        setShowNewPaper(false);
    };

    return (
        <AppDialog
            isOpen={isOpen}
            onClose={onClose}
            size="4xl"
            title={t('dialogs.ui.newSheet')}
            footer={
                <>
                    <DialogButton variant="ghost" onClick={onClose}>{t('dialogs.ui.cancel')}</DialogButton>
                    <DialogButton variant="primary" onClick={handleCreate}>{t('dialogs.ui.oK')}</DialogButton>
                </>
            }
        >
                    <Flex gap={6} align="flex-start">
                        {/* Template gallery */}
                        <Box flex={1} minW={0}>
                            <Text fontSize="xs" fontWeight="bold" mb={2} color="text.primary">{t('dialogs.ui.selectASheetTemplate')}</Text>
                            <Flex wrap="wrap" gap={3}>
                                {allTemplates.map((t, i) => (
                                    <TemplateCard
                                        key={`${t.titleBlockId}-${i}`}
                                        template={t}
                                        isSelected={!showNewPaper && selectedTemplate.titleBlockId === t.titleBlockId}
                                        onSelect={() => selectTemplate(t)}
                                    />
                                ))}
                            </Flex>
                        </Box>

                        {/* Right column: details + new paper type */}
                        <Box w="280px" flexShrink={0}>
                            <Text fontSize="xs" fontWeight="bold" mb={2} color="text.primary">{t('dialogs.ui.sheet')}</Text>
                            <FormControl mb={2}>
                                <FormLabel fontSize="xs">{t('dialogs.ui.sheetNumber')}</FormLabel>
                                <Input
                                    size="sm"
                                    value={sheetNumber}
                                    onChange={(e) => setSheetNumber(e.target.value)}
                                    placeholder={t('dialogs.ui.a101')}
                                />
                            </FormControl>
                            <FormControl mb={3}>
                                <FormLabel fontSize="xs">{t('dialogs.ui.sheetName')}</FormLabel>
                                <Input
                                    size="sm"
                                    value={sheetName}
                                    onChange={(e) => setSheetName(e.target.value)}
                                    placeholder={t('dialogs.ui.groundFloorPlan')}
                                />
                            </FormControl>

                            <Box
                                p={2}
                                bg="bg.canvas"
                                border="1px solid"
                                borderColor="border.base"
                                borderRadius="sm"
                                mb={3}
                            >
                                <Text fontSize="xs" fontWeight="bold">{showNewPaper ? paperName || 'New Paper Type' : selectedTemplate.name}</Text>
                                <Text fontSize="xs" color="text.secondary">
                                    {showNewPaper
                                        ? `${Math.round(parseFloat(paperWidth) || 0)} x ${Math.round(parseFloat(paperHeight) || 0)} mm`
                                        : `${selectedTemplate.sizeLabel} — ${SHEET_STYLE_LABELS[selectedTemplate.style]}`}
                                </Text>
                                {!showNewPaper && (
                                    <Text fontSize="xs" color="text.secondary" noOfLines={2} mt={1}>
                                        {selectedTemplate.description}
                                    </Text>
                                )}
                            </Box>

                            <Divider mb={3} />

                            <Button
                                size="xs"
                                leftIcon={<Plus size={14} />}
                                variant={showNewPaper ? 'solid' : 'outline'}
                                colorScheme={showNewPaper ? 'blue' : 'gray'}
                                onClick={() => setShowNewPaper(!showNewPaper)}
                                w="100%"
                            >{t('dialogs.ui.newPaperType')}</Button>

                            {showNewPaper && (
                                <Box mt={3}>
                                    <FormControl mb={2}>
                                        <FormLabel fontSize="xs">{t('dialogs.ui.paperName')}</FormLabel>
                                        <Input
                                            size="sm"
                                            value={paperName}
                                            onChange={(e) => setPaperName(e.target.value)}
                                            placeholder={t('dialogs.ui.custom500X400')}
                                        />
                                    </FormControl>
                                    <Flex gap={2} mb={2}>
                                        <FormControl>
                                            <FormLabel fontSize="xs">{t('dialogs.ui.widthMm')}</FormLabel>
                                            <NumberInput size="sm" min={100} max={3000} value={paperWidth} onChange={(v) => setPaperWidth(v)}>
                                                <NumberInputField />
                                            </NumberInput>
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize="xs">{t('dialogs.ui.heightMm')}</FormLabel>
                                            <NumberInput size="sm" min={100} max={3000} value={paperHeight} onChange={(v) => setPaperHeight(v)}>
                                                <NumberInputField />
                                            </NumberInput>
                                        </FormControl>
                                    </Flex>
                                    <Text fontSize="xs" color="text.secondary">{t('dialogs.ui.thePaperTypeIsSavedToTheProjectAndAppearsInThisGallery')}</Text>
                                </Box>
                            )}
                        </Box>
                    </Flex>
        </AppDialog>
    );
}

/**
 * Template card with a miniature paper preview that reflects the sheet's
 * aspect ratio and its letterhead style (border, band, title block strip).
 */
function TemplateCard({ template, isSelected, onSelect }: { template: SheetTemplate; isSelected: boolean; onSelect: () => void }) {
    const maxW = 108;
    const maxH = 82;
    const scale = Math.min(maxW / template.widthMm, maxH / template.heightMm);
    const pw = template.widthMm * scale;
    const ph = template.heightMm * scale;
    const tbW = Math.max(pw * 0.18, 10); // title block strip proportion

    return (
        <Box
            as="button"
            w="128px"
            p={2}
            textAlign="center"
            border="1px solid"
            borderColor={isSelected ? 'brand.500' : 'border.base'}
            bg={isSelected ? 'bg.active' : 'transparent'}
            borderRadius="sm"
            _hover={{ borderColor: isSelected ? 'brand.500' : 'brand.300', bg: isSelected ? 'bg.active' : 'bg.hover' }}
            onClick={onSelect}
            role="group"
        >
            {/* Paper preview */}
            <Flex h="90px" align="center" justify="center" mb={1}>
                <Box
                    width={`${pw}px`}
                    height={`${ph}px`}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.400"
                    position="relative"
                    overflow="hidden"
                    boxShadow={isSelected ? '0 0 0 2px var(--chakra-colors-brand-500)' : 'sm'}
                >
                    {/* Inner frame */}
                    <Box position="absolute" inset={`${Math.max(ph * 0.03, 1.5)}px`} border="1px solid" borderColor="gray.700" />
                    {/* Style flourishes */}
                    {template.style === 'corporate' && (
                        <Box position="absolute" top="0" left="0" right="0" height={`${Math.max(ph * 0.08, 3)}px`} bg="brand.500" />
                    )}
                    {template.style === 'minimal' && (
                        <Box position="absolute" bottom="4px" left="4px" width={`${pw * 0.25}px`} borderBottom="1px solid" borderColor="gray.500" />
                    )}
                    {template.style === 'engineering' && (
                        <>
                            <Box position="absolute" top={`${ph * 0.05}px`} left={`${pw * 0.05}px`} right={`${pw * 0.05}px`} borderTop="1px dashed" borderColor="gray.500" />
                            <Box position="absolute" bottom={`${ph * 0.05}px`} left={`${pw * 0.05}px`} right={`${pw * 0.05}px`} borderBottom="1px dashed" borderColor="gray.500" />
                        </>
                    )}
                    {/* Title block strip on the right edge */}
                    <Box
                        position="absolute"
                        top="0"
                        right="0"
                        width={`${tbW}px`}
                        height="100%"
                        borderLeft="1px solid"
                        borderColor="gray.700"
                    >
                        <Box mt="2px" mx="2px" height="3px" bg="gray.600" />
                        <Box mt="2px" mx="2px" height="2px" bg="gray.300" />
                        <Box mt="2px" mx="2px" height="2px" bg="gray.300" />
                    </Box>
                </Box>
            </Flex>
            <Flex align="center" justify="center" gap={1}>
                <Icon as={FileText} boxSize={3} color="icon.base" />
                <Text fontSize="xs" color="text.primary" noOfLines={1} fontWeight={isSelected ? 'bold' : 'normal'}>
                    {template.name}
                </Text>
            </Flex>
            <Text fontSize="2xs" color="text.secondary">{template.sizeLabel}</Text>
        </Box>
    );
}
